import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Select, Tag, Skeleton } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getLeads } from "../features/customers/customerSlice";
import { getAllUsers } from "../features/auth/authSlice";
import "../styles/team.css";

/* ================= DATE HELPERS ================= */

const now = new Date();
const currentYear = now.getFullYear();

const monthOptions = [
  { value: "all", label: "All Months" },
  { value: 0, label: "January" },
  { value: 1, label: "February" },
  { value: 2, label: "March" },
  { value: 3, label: "April" },
  { value: 4, label: "May" },
  { value: 5, label: "June" },
  { value: 6, label: "July" },
  { value: 7, label: "August" },
  { value: 8, label: "September" },
  { value: 9, label: "October" },
  { value: 10, label: "November" },
  { value: 11, label: "December" },
];

const getWeekOfMonth = (dateString) => {
  const date = new Date(dateString);
  return Math.ceil(date.getDate() / 7);
};

const getWeeksInMonth = (year, month) => {
  if (month === "all") return 5;
  const lastDay = new Date(year, month + 1, 0).getDate();
  return Math.ceil(lastDay / 7);
};

const lostStatuses = [
  "lost",
  "bought_elsewhere",
  "not_interested",
  "no_budget",
  "no_response",
];

/* ================= TEAM CARD ================= */

const TeamCard = ({ member }) => {
  const leadsReceived = member.leads.length;

  const followUps = member.leads.filter(
    (lead) => lead.status === "follow_up"
  ).length;

  const contacted = member.leads.filter(
    (lead) => lead.status !== "new"
  ).length;

  const appointments = member.leads.filter(
    (lead) => lead.status === "appointment_booked"
  ).length;

  const dealsClosed = member.leads.filter(
    (lead) => lead.status === "deal_closed"
  ).length;

  const dealsLost = member.leads.filter((lead) =>
    lostStatuses.includes(lead.status)
  ).length;

  const displayName =
    member.agentName ||
    `${member.firstname || ""} ${member.lastname || ""}`.trim() ||
    "Team Member";

  return (
    <Link to={`/admin/team/${member._id}`} style={{ textDecoration: "none" }}>
      <div className="bg-white p-4 rounded-3 mb-4 shadow-sm">
        <div className="d-flex align-items-center gap-3 mb-3">
          <Avatar size={60} icon={<UserOutlined />} />
          <div>
            <h6 className="mb-0">{displayName}</h6>
            <small className="text-muted text-capitalize">
              {member.role || "employee"}
            </small>
          </div>
        </div>

        <div className="tags gap-2">
          <Tag color="purple">Leads: {leadsReceived}</Tag>
          <Tag color="orange">Follow Ups: {followUps}</Tag>
          <Tag color="cyan">Contacted: {contacted}</Tag>
          <Tag color="green">Appointments: {appointments}</Tag>
          <Tag color="blue">Deals Closed: {dealsClosed}</Tag>
          <Tag color="red">Lost: {dealsLost}</Tag>
        </div>
      </div>
    </Link>
  );
};

/* ================= TEAM SKELETON CARD ================= */

const TeamCardSkeleton = () => {
  return (
    <div className="bg-white p-4 rounded-3 mb-4 shadow-sm">
      <div className="d-flex align-items-center gap-3 mb-3">
        <Skeleton.Avatar active size={60} shape="circle" />
        <div style={{ flex: 1 }}>
          <Skeleton.Input
            active
            size="small"
            style={{ width: "25%", marginBottom: 10 , height:15}}
          />
          <br />
          <Skeleton.Input active size="small" style={{ width: "15%",height:13 }} />
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2">
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <Skeleton.Button
            key={item}
            active
            size="small"
            style={{ width: 110, height: 18, borderRadius: 2 }}
          />
        ))}
      </div>
    </div>
  );
};

const Team = () => {
  const dispatch = useDispatch();

  const leadState = useSelector((state) => state.customer.leads || []);
  const customerLoading = useSelector((state) => state.customer.isLoading);
  const users = useSelector((state) => state.auth.users || []);
  const authLoading = useSelector((state) => state.auth.isLoading);

  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedWeek, setSelectedWeek] = useState("all");

  useEffect(() => {
    dispatch(getLeads());
    dispatch(getAllUsers());
  }, [dispatch]);

  const totalWeeksInMonth = getWeeksInMonth(currentYear, selectedMonth);

  const weekOptions = [
    { value: "all", label: "All Weeks" },
    ...Array.from({ length: totalWeeksInMonth }, (_, i) => ({
      value: i + 1,
      label: `Week ${i + 1}`,
    })),
  ];

  const filteredTeam = useMemo(() => {
    const teamUsers = users.filter((user) =>
      ["employee"].includes(String(user.role || "").toLowerCase())
    );

    return teamUsers.map((member) => {
      const memberNames = [
        member.agentName,
        member.firstname,
        `${member.firstname || ""} ${member.lastname || ""}`.trim(),
      ]
        .filter(Boolean)
        .map((name) => String(name).trim().toLowerCase());

      const memberLeads = leadState.filter((lead) => {
        const leadDate = new Date(lead.createdAt);
        const leadMonth = leadDate.getMonth();
        const leadWeek = getWeekOfMonth(lead.createdAt);
        const leadAgentName = String(lead.agentName || "")
          .trim()
          .toLowerCase();

        const matchesAgent = memberNames.includes(leadAgentName);
        const matchesMonth =
          selectedMonth === "all" || leadMonth === selectedMonth;
        const matchesWeek =
          selectedWeek === "all" || leadWeek === selectedWeek;

        return matchesAgent && matchesMonth && matchesWeek;
      });

      return {
        ...member,
        leads: memberLeads,
      };
    });
  }, [users, leadState, selectedMonth, selectedWeek]);

  const selectedMonthLabel =
    selectedMonth === "all"
      ? "All Months"
      : monthOptions.find((m) => m.value === selectedMonth)?.label;

  const selectedWeekLabel =
    selectedWeek === "all" ? "All Weeks" : `Week ${selectedWeek}`;

  const isTeamLoading = customerLoading || authLoading;

  return (
    <div>
      <h3 className="mb-1 title">Carman Sales Team</h3>

      <p className="text-muted mb-4">
        Showing Data for:{" "}
        <strong>
          {selectedMonthLabel}, {selectedWeekLabel}, {currentYear}
        </strong>
      </p>

      <div className="mb-4 d-flex gap-3 flex-wrap">
        <Select
          placeholder="Filter by Month"
          style={{ width: 180 }}
          value={selectedMonth}
          onChange={(value) => {
            setSelectedMonth(value);
            setSelectedWeek("all");
          }}
          options={monthOptions}
        />

        <Select
          placeholder="Filter by Week"
          style={{ width: 180 }}
          value={selectedWeek}
          onChange={(value) => setSelectedWeek(value)}
          options={weekOptions}
        />
      </div>

      <div className="row">
        {isTeamLoading
          ? Array.from({ length: 6 }).map((_, index) => (
              <div className="col-md-4" key={index}>
                <TeamCardSkeleton />
              </div>
            ))
          : filteredTeam.map((member) => (
              <div className="col-md-4" key={member._id}>
                <TeamCard member={member} />
              </div>
            ))}
      </div>
    </div>
  );
};

export default Team;